import { Search, X } from "lucide-react";
import { useState, type JSX } from "react";

import type { MemberDTO } from "../../../common/api/types.js";
import { Avatar } from "../../../common/components/Avatar.js";
import { useModalFocus } from "../../../common/components/useModalFocus.js";

export function NewChatDialog({ members, busy, error, onSelect, onClose }: {
  members: MemberDTO[];
  busy: boolean;
  error: string | null;
  onSelect: (member: MemberDTO) => void;
  onClose: () => void;
}): JSX.Element {
  const panel = useModalFocus(onClose);
  const [filter, setFilter] = useState("");
  const needle = filter.trim().toLocaleLowerCase("de-DE");
  const visible = members.filter((member) =>
    `${member.displayName} ${member.email}`.toLocaleLowerCase("de-DE").includes(needle),
  );

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 sm:items-center sm:p-6">
      <button type="button" aria-label="Neue Unterhaltung schließen" onClick={onClose} className="absolute inset-0" />
      <section ref={panel} role="dialog" aria-modal="true" aria-labelledby="new-chat-title" tabIndex={-1} className="relative flex max-h-[80vh] w-full max-w-md flex-col rounded-t-2xl border border-line bg-surface-raised pb-[env(safe-area-inset-bottom)] shadow-2xl sm:rounded-2xl sm:pb-0">
        <div className="flex items-center gap-2 border-b border-line p-4">
          <h2 id="new-chat-title" className="flex-1 text-base font-semibold text-ink">Neue Unterhaltung</h2>
          <button type="button" onClick={onClose} aria-label="Schließen" className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink"><X className="h-5 w-5" /></button>
        </div>
        <label className="relative m-3 mb-1 block">
          <span className="sr-only">Person suchen</span>
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-ink-muted" />
          <input autoFocus value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Name oder Mailadresse" className="w-full rounded-lg border border-line bg-surface-sunken py-2 pl-8 pr-3 text-sm text-ink outline-none focus:border-brand" />
        </label>
        <div className="min-h-32 overflow-y-auto p-3">
          {visible.length === 0 ? <p className="p-6 text-center text-sm text-ink-muted">Keine passende Person.</p> : null}
          <ul className="space-y-1">
            {visible.map((member) => (
              <li key={member.userId}>
                <button type="button" disabled={busy} onClick={() => onSelect(member)} className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-surface-sunken disabled:opacity-50">
                  <Avatar displayName={member.displayName} avatarUrl={member.avatarUrl} presence={member.presence} />
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-ink">{member.displayName}</span><span className="block truncate text-xs text-ink-muted">{member.role === "GUEST" ? `Gast · ${member.email}` : member.kind === "AGENT" ? `Agent · ${member.email}` : member.email}</span></span>
                </button>
              </li>
            ))}
          </ul>
          {error ? <p role="alert" className="mt-2 text-sm text-danger">{error}</p> : null}
        </div>
      </section>
    </div>
  );
}
